import * as stylex from "@stylexjs/stylex";
import { Bell, CalendarDays, Download } from "lucide-react";
import { copy } from "@/copy";
import type { ShownBackground } from "@/domain/background";
import type { AccountSettings, Season } from "@/domain/schemas";
import "@/theme/tokens.stylex";
import { colors, radii, shadows, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { Radio } from "@/ui/Radio";
import { SettingsRow } from "@/ui/SettingsRow";
import { seasonIcons } from "@/ui/seasonIcons";
import { locationSummary } from "../location";
import type { ThisPhone } from "../thisPhone";
import type { SettingsAccount } from "./AccountCard";
import { ApiKeyRow, type ApiKeyRowProps } from "./ApiKeyRow";
type SettingsPanelProps = {
  account: SettingsAccount;
  settings: AccountSettings;
  season: Season;
  background: ShownBackground;
  onChange: (change: Partial<AccountSettings>) => void;
  onOpenBackground: () => void;
  onOpenLocation: () => void;
  lastImport?: string;
  onOpenImport: () => void;
  online: boolean;
  upcomingEvents: number;
  savedEvents?: number;
  onExportEvents: () => void;
  apiKey: Omit<ApiKeyRowProps, "online">;
  phone: ThisPhone;
  onAllowPush: () => void;
  onTurnOffPush: () => void;
};
const SEASON_OPTIONS = [{
  value: "auto" as const,
  label: copy.settings.season.autoLabel,
  icon: CalendarDays
}, ...(["spring", "summer", "fall", "winter"] as const).map(value => ({
  value,
  label: copy.season[value],
  icon: seasonIcons[value]
}))];
const styles = {
  wrap: {
    kwnvtZ: "x1a02dak",
    kmuXW: "xs83m0k",
    khm7nJ: "x3pnbk8",
    $$css: true
  },
  ring: {
    kGVxlE: "xnfxhx2",
    $$css: true
  }
};
export function SettingsPanel({
  account,
  settings,
  season,
  background,
  onChange,
  onOpenBackground,
  onOpenLocation,
  lastImport,
  onOpenImport,
  online,
  upcomingEvents,
  savedEvents,
  onExportEvents,
  apiKey,
  phone,
  onAllowPush,
  onTurnOffPush
}: SettingsPanelProps) {
  const text = copy.settings;
  return <div className="x78zum5 xdt5ytf xddi9bj xeuugli xh540kj xwe7p7i x16krsup x9f619">
      <div className="x78zum5 xdt5ytf x152posx">
        <Eyebrow>{text.eyebrow}</Eyebrow>
        <h2 className="x1ghz6dp x4c74qz x43273g xh92tn4 xju0f8q">{text.title}</h2>
        <SignedIn account={account} />
      </div>
      <div className="x78zum5 xdt5ytf">
        <SettingsRow title={text.location.title} description={text.location.body} onClick={onOpenLocation}>
          {locationSummary(settings.weatherLocation)}
        </SettingsRow>
        <SettingsRow title={text.season.title} description={seasonSubtitle(settings.season, season)} align="start">
          <Radio label={text.season.title} options={SEASON_OPTIONS} value={settings.season} onChange={value => onChange({
          season: value
        })} direction="column" />
        </SettingsRow>
        <SettingsRow title={text.background.title} description={text.background.body} onClick={onOpenBackground}>
          {backgroundSummary(background)}
        </SettingsRow>
        <SettingsRow title={text.clock.title} description={text.clock.body}>
          <Radio label={text.clock.title} options={text.clock.options} value={settings.clock} onChange={value => onChange({
          clock: value
        })} />
        </SettingsRow>
        <SettingsRow title={text.temperature.title} description={text.temperature.body}>
          <Radio label={text.temperature.title} options={text.temperature.options} value={settings.tempUnit} onChange={value => onChange({
          tempUnit: value
        })} />
        </SettingsRow>
        <NotificationRows settings={settings} onChange={onChange} phone={phone} onAllowPush={onAllowPush} onTurnOffPush={onTurnOffPush} />
        <SettingsRow title={text.hideSidebar.title} description={text.hideSidebar.body} checked={settings.hideSidebarWhenIdle} onCheckedChange={checked => onChange({
        hideSidebarWhenIdle: checked
      })} />
        <SettingsRow title={text.importEvents.title} description={text.importEvents.body} onClick={onOpenImport}>
          {lastImport && text.importEvents.last(lastImport)}
        </SettingsRow>
        <SettingsRow title={text.exportEvents.title} description={text.exportEvents.body} status={exportStatus(online, upcomingEvents, savedEvents)}>
          <Button variant="secondary" icon={Download} disabled={!online || upcomingEvents === 0} onClick={onExportEvents} xstyle={styles.ring}>
            {text.exportEvents.download}
          </Button>
        </SettingsRow>
        <ApiKeyRow {...apiKey} online={online} />
      </div>
    </div>;
}
function NotificationRows({
  settings,
  onChange,
  phone,
  onAllowPush,
  onTurnOffPush
}: Pick<SettingsPanelProps, "settings" | "onChange" | "phone" | "onAllowPush" | "onTurnOffPush">) {
  const text = copy.settings.notifications;
  return <>
      <SettingsRow title={text.title} description={text.sub(settings.notifyLead)}>
        <Radio label={text.group} options={text.options} value={settings.notifyLead} onChange={value => onChange({
        notifyLead: value
      })} xstyle={styles.wrap} />
      </SettingsRow>
      <SettingsRow title={text.chime} description={text.chimeBody} checked={settings.chime} onCheckedChange={checked => onChange({
      chime: checked
    })} />
      {phone.kind !== "unsupported" && <SettingsRow title={text.thisPhone} description={text.thisPhoneBody}>
          <ThisPhoneControl phone={phone} onAllow={onAllowPush} onTurnOff={onTurnOffPush} />
        </SettingsRow>}
    </>;
}
function ThisPhoneControl({
  phone,
  onAllow,
  onTurnOff
}: {
  phone: Exclude<ThisPhone, {
    kind: "unsupported";
  }>;
  onAllow: () => void;
  onTurnOff: () => void;
}) {
  const text = copy.settings.notifications;
  switch (phone.kind) {
    case "ask":
    case "asking":
      return <Button variant="secondary" icon={Bell} disabled={phone.kind === "asking"} onClick={onAllow} xstyle={styles.ring}>
          {text.allow}
        </Button>;
    case "denied":
      return <span className="xxc7z9f x14c3fy2 x1dld7ex x1fzhlzt">{text.blocked}</span>;
    case "on":
    case "turning-off":
      return <span className="x78zum5 x6s0dn4 x1hu4l6k x1iig92y xhxcbl2">
          {text.on}
          <Button variant="ghost" disabled={phone.kind === "turning-off"} onClick={onTurnOff}>
            {text.turnOff}
          </Button>
        </span>;
  }
}
function exportStatus(online: boolean, upcoming: number, saved: number | undefined): string {
  const text = copy.settings.exportEvents;
  if (!online) return text.offline;
  return saved === undefined ? text.upcoming(upcoming) : text.saved(saved);
}
function seasonSubtitle(setting: AccountSettings["season"], season: Season): string {
  const name = copy.season[season];
  return setting === "auto" ? copy.settings.season.following(name) : copy.settings.season.fixed(name);
}
function backgroundSummary(background: ShownBackground): string {
  const text = copy.settings.background;
  switch (background.mode) {
    case "season":
      return text.season(copy.season[background.drawing]);
    case "blank":
      return text.blank;
    case "photo":
      return text.photo(background.photo.title);
    case "slideshow":
      return text.slideshow(copy.background.interval[background.rotate]);
  }
}
function SignedIn({
  account
}: {
  account: SettingsAccount;
}) {
  return <span className="x19d36u7 x15i68ds">
      {copy.settings.signedInAs}{" "}
      {account.name === null ? account.email : <>
          <span className="xfbjfs5">{account.name}</span>
          {copy.settings.signedInEmail(account.email)}
        </>}
    </span>;
}